import React, { useEffect, useState } from "react";
import "./imageModal.css";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import { useMutation } from "@apollo/client";
import { UPDATE_IMG_NOTE } from "../../../../utils/GraphQL/mutations";

/**
 * Enlarged image, with an optional editable note.
 *
 * Opened from five places: client photos, the email modal, the calendar's
 * event-detail popup, the message image shuffler, and the job-form review
 * page. Two of those pass `allowNoteUpdate` and get the note row.
 */
const ImageModal = ({
  modalOpen,
  handleClose,
  imgSrc,
  allowNoteUpdate,
  passedNote,
  imgId,
  handleSnack,
  updateImgArr,
  /**
   * Which rung of the overlay ladder. Replaces a raw `zIndex` prop that
   * existed because MUI's Modal defaults to 1300, so a caller opening this
   * from a higher overlay had to hand-pick a number above it. The ladder
   * expresses that as a position instead: "raised" means "opened from
   * something already on top".
   */
  layer = "base",
  /**
   * Read-only text shown under the photo (the calendar popup passes the
   * photo's note). Ignored when `allowNoteUpdate` is set, because the note
   * row already shows it there.
   */
  caption,
}) => {
  const [note, setNote] = useState("");
  const showCaption = !allowNoteUpdate && !!caption?.trim();
  const [updateNoteGQL] = useMutation(UPDATE_IMG_NOTE);

  useEffect(() => {
    setNote(passedNote?.length ? passedNote : "");
  }, [passedNote]);

  const handleSaveNote = async () => {
    const saved = await updateNoteGQL({
      variables: { imgId, note },
    });

    if (saved) {
      if (handleSnack) {
        handleSnack("Updated image note successfully");
        updateImgArr(imgId, note);
      }
      handleClose();
    }
  };

  return (
    <BdrModal
      open={modalOpen}
      onClose={handleClose}
      title={allowNoteUpdate ? "Image & Note" : "Image"}
      variant="panel"
      maxWidth="none"
      panelClassName="img-modal-panel"
      layer={layer}
      actions={
        allowNoteUpdate ? (
          <>
            <BdrButton onClick={handleClose}>Cancel</BdrButton>
            {/* solid: variant="panel" is the saturated surface (§6b) */}
            <BdrButton variant="confirm-solid" onClick={handleSaveNote}>
              Save
            </BdrButton>
          </>
        ) : undefined
      }
    >
      {imgSrc ? (
        <div className="img-modal-body">
          {/* Click-to-close is kept from the original, but it is no longer
              the ONLY way out — the shell supplies a × and Esc. */}
          <img
            className={
              showCaption ? "img-modal-img img-modal-img--captioned" : "img-modal-img"
            }
            onClick={handleClose}
            src={imgSrc}
            alt="Enlarged"
          />
          {showCaption && <p className="img-modal-caption">{caption.trim()}</p>}
          {allowNoteUpdate && (
            <BdrInput
              className="img-modal-note"
              type="text"
              label="Note"
              inputMode="text"
              name="note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          )}
        </div>
      ) : (
        <p className="bdr-empty">Image Unavailable</p>
      )}
    </BdrModal>
  );
};

export default ImageModal;
